<script setup lang="ts">
import { dateTimeFormatter } from '@/helpers/ep/formatters'
import { getMinerHistory } from '@/services/rc.service'
import type { IMinerHistoryFilters } from '@/types/rc/filters'
import { PartType } from '@/types/rc/miners/PartType'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { Icon } from '@iconify/vue'
import { useQuery } from '@tanstack/vue-query'
import { DateTime } from 'luxon'
import { computed, reactive, ref } from 'vue'

const open = defineModel<boolean>()
const props = defineProps<{ minerId: number }>()
const enableQuery = ref<boolean>(false)

const dateRangeFilter = ref<[Date, Date] | undefined>()
const partTypeOptions = ref<string[]>([
  PartType.Miner,
  PartType.ControlBoard,
  PartType.PowerSupply,
  PartType.Hashboard,
  PartType.Fan,
])

// Compute message to display when table is empty.
const tableEmptyText = computed(() => {
  return isFetching.value ? 'Loading...' : 'No records'
})

const filters = reactive<IMinerHistoryFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[0],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  start_date: undefined,
  end_date: undefined,
  part_name: undefined,
})

const { isFetching, isPending, isError, isRefetching, data, error, refetch } = useQuery({
  queryKey: ['miner-history', props.minerId, filters],
  queryFn: () => getMinerHistory(props.minerId, filters),
  placeholderData: (prev: any) => prev,
  enabled: enableQuery,
})

/**
 * Handle `el-datepicker` range change.
 *
 * @param newRange the new selected date range.
 */
const handleDateRangeChange = async (newRange: Array<Date> | null) => {
  if (!newRange) {
    filters.start_date = undefined
    filters.end_date = undefined
    return
  }

  filters.start_date = DateTime.fromJSDate(newRange[0]).toISO() ?? undefined
  filters.end_date = DateTime.fromJSDate(newRange[1]).toISO() ?? undefined
}

/**
 * Enable query and fetch record
 */
const handleOpen = () => {
  enableQuery.value = true
  refetch()
}
</script>

<template>
  <el-dialog v-model="open" :width="1000" title="Miner History" :show-close="false" @open="handleOpen">
    <div class="layout">
      <rl-error-result v-if="isError && !isRefetching" :error="error">
        <el-button @click="() => refetch()">
          <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
        </el-button>
      </rl-error-result>
      <template v-else>
        <div class="history__controls">
          <div class="history__filters">
            <el-select
              v-model="filters.part_name"
              style="width: 232px"
              :disabled="isPending"
              placeholder="Part Type"
              clearable
            >
              <el-option v-for="partType in partTypeOptions" :key="partType" :label="partType" :value="partType" />
            </el-select>
            <el-date-picker
              v-model="dateRangeFilter"
              :disabled="isPending"
              type="datetimerange"
              range-separator="to"
              start-placeholder="Start date"
              end-placeholder="End date"
              format="YYYY-MM-DD HH:mm:ss"
              date-format="YYYY/MM/DD ddd"
              time-format="A hh:mm:ss"
              editable
              clearable
              @change="handleDateRangeChange"
            />
          </div>
        </div>
        <el-table v-loading="isFetching" :height="500" :data="data?.data" :empty-text="tableEmptyText" stripe>
          <el-table-column prop="part_name" label="Part" :min-width="112" />
          <el-table-column prop="action" label="Action" :min-width="312" />
          <el-table-column prop="action_user" label="User" :min-width="188" />
          <el-table-column prop="action_timestamp" :formatter="dateTimeFormatter" label="Time" :min-width="140" />
        </el-table>
        <el-pagination
          v-if="data && data.total_pages > 0"
          v-model:current-page="filters.page"
          v-model:page-size="filters.per_page"
          class="history__pagination"
          :page-sizes="DEFAULT_PAGE_SIZES"
          :page-count="data.total_pages"
          :total="data.total_records"
          :disabled="isFetching"
          :layout="DEFAULT_PAGINATION_LAYOUT"
          background
        />
      </template>
    </div>
  </el-dialog>
</template>

<style scoped lang="scss">
.history {
  &__controls {
    display: inline-flex;
    justify-content: space-between;
  }

  &__filters {
    display: flex;
    gap: var(--spacing-spacing-sm);
  }

  &__pagination {
    align-self: flex-end;
  }
}

.layout {
  gap: var(--spacing-spacing-xl);
  display: flex;
  flex-direction: column;
}
</style>
